import { comparisonTable, platformComparison } from "@/mocks/categories";

export default function ContentSections() {
  return (
    <>
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Why Most Buyers Lose Control of Their Orders
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Without a centralized <strong>GTBuy spreadsheet</strong> system, managing orders across multiple proxy shopping platforms becomes chaotic and error-prone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: "ri-file-list-3-line", title: "Scattered Tracking", desc: "Manually checking GTBuy, Kakobuy and Superbuy separately wastes hours each week." },
              { icon: "ri-ship-line", title: "Missed Shipments", desc: "Without a unified Kakobuy spreadsheet, shipping updates slip through the cracks." },
              { icon: "ri-error-warning-line", title: "Confusing Structures", desc: "Each platform uses different formats — a Superbuy spreadsheet unifies everything." },
              { icon: "ri-database-2-line", title: "No Central Hub", desc: "Critical order data scattered across platforms leads to financial confusion and delays." },
            ].map((item) => (
              <div key={item.title} className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200 transition-all duration-300 cursor-default">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              A Unified Spreadsheet System for GTBuy, Kakobuy &amp; Superbuy
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Our <strong>GTBuy spreadsheet</strong> system consolidates all your proxy shopping data into one intelligent, easy-to-use platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-14">
            {[
              { icon: "ri-folders-line", title: "Track All Orders", desc: "Import and monitor every purchase from GTBuy, Kakobuy, and Superbuy in one unified spreadsheet view." },
              { icon: "ri-truck-line", title: "Real-time Shipping", desc: "Automatically sync shipping status updates so you never miss a delivery notification again." },
              { icon: "ri-scales-3-line", title: "Compare Prices", desc: "Side-by-side price comparison across platforms helps you make smarter purchasing decisions." },
              { icon: "ri-settings-3-line", title: "Auto Organization", desc: "Intelligent tagging and categorization keeps your Kakobuy spreadsheet and Superbuy data perfectly organized." },
              { icon: "ri-shield-check-line", title: "Error Reduction", desc: "Automated validation reduces manual entry errors that cost time and money in order management." },
              { icon: "ri-time-line", title: "Save Hours Weekly", desc: "What used to take hours of manual tracking now takes minutes with our spreadsheet system." },
            ].map((item) => (
              <div key={item.title} className="group p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-default">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-900 whitespace-nowrap">Feature</th>
                  <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-500 whitespace-nowrap">Manual Tracking</th>
                  <th className="text-left px-5 py-4 font-heading font-semibold text-primary-600 whitespace-nowrap">Our Spreadsheet System</th>
                </tr>
              </thead>
              <tbody>
                {comparisonTable.map((row, idx) => (
                  <tr key={row.feature} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                    <td className="px-5 py-3.5 font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                    <td className="px-5 py-3.5 text-foreground-500">{row.manual}</td>
                    <td className="px-5 py-3.5 text-primary-700 font-medium">{row.system}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              GTBuy vs Kakobuy vs Superbuy Spreadsheet Comparison
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Understand how each platform&apos;s <strong>spreadsheet</strong> capabilities compare — and why our unified system combines the best of all three.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-50">
                  <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-900 whitespace-nowrap">Platform</th>
                  <th className="text-center px-5 py-4 font-heading font-semibold text-primary-600 whitespace-nowrap">GTBuy Spreadsheet</th>
                  <th className="text-center px-5 py-4 font-heading font-semibold text-accent-600 whitespace-nowrap">Kakobuy Spreadsheet</th>
                  <th className="text-center px-5 py-4 font-heading font-semibold text-secondary-600 whitespace-nowrap">Superbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {platformComparison.map((row, idx) => (
                  <tr key={row.feature} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                    <td className="px-5 py-3.5 font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                    <td className="px-5 py-3.5 text-center text-foreground-700">{row.gtbuy}</td>
                    <td className="px-5 py-3.5 text-center text-foreground-700">{row.kakobuy}</td>
                    <td className="px-5 py-3.5 text-center text-foreground-700">{row.superbuy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              How the Spreadsheet System Works
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Getting started with your <strong>GTBuy spreadsheet</strong> takes just four simple steps.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-12 left-1/2 w-[75%] -translate-x-1/2 h-0.5 bg-background-200"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { step: "01", icon: "ri-download-2-line", title: "Import Your Orders", desc: "Pull order data from GTBuy, Kakobuy, or Superbuy into the spreadsheet with one click." },
                { step: "02", icon: "ri-price-tag-3-line", title: "Assign Platform Tags", desc: "Tag each entry as GTBuy, Kakobuy, or Superbuy for automatic cross-platform organization." },
                { step: "03", icon: "ri-refresh-line", title: "Auto-Update Status", desc: "The system periodically syncs shipping and order status changes from all connected platforms." },
                { step: "04", icon: "ri-bar-chart-2-line", title: "Monitor Everything", desc: "View dashboards, compare prices, and track all orders in real time from one central interface." },
              ].map((item) => (
                <div key={item.step} className="relative flex flex-col items-center text-center p-6 rounded-xl bg-background-100 border border-background-200/70 z-10">
                  <span className="font-heading text-3xl font-bold text-primary-200 mb-3">{item.step}</span>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-white mb-4">
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}